import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Education } from '../models/education.model';

const TITLES = [
  'Data Protection Officer (GDPR)',
  'Microsoft Excel basis + Excel geavanceerd',
  'Projectmanagement Professional',
  'Digital Marketing Specialist',
  'HR Management',
  'Financieel Adviseur',
  'UI/UX Design',
  'Agile Scrum Master',
];
const INSTITUTIONS = ['NHA Afstandsonderwijs', 'LOI', 'NCOI', 'Hogeschool Utrecht', 'ROC Amsterdam', 'Tilburg University'];
const DURATIONS = ['4 maanden', '8 weken', '6 maanden', '1 jaar', '2 jaar'];
const FORMATS = ['E-learning en op locatie', 'Online', 'Deeltijd', 'Voltijd', 'Avondopleiding'];
const LOCATIONS = ['Online', 'Amsterdam', 'Utrecht', 'Rotterdam', 'Den Haag', 'Eindhoven'];
const LEVELS = ['MBO', 'HBO', 'WO', 'Cursus'];
const START_DATES = ['Start meteen', 'September 2024', 'Januari 2025', 'Flexibel'];

const DESCRIPTIONS: Record<number, string> = {
  1: `**Over de opleiding**

Deze opleiding bereidt je voor op de rol van Data Protection Officer (DPO) binnen organisaties. Je leert alles over de Algemene Verordening Gegevensbescherming (AVG/GDPR) en hoe je privacy en gegevensbescherming waarborgt in je organisatie.

Als DPO ben je verantwoordelijk voor het toezicht op de naleving van de privacywetgeving binnen een organisatie. Je adviseert over privacy-impact assessments, behandelt datalekken en bent het aanspreekpunt voor de Autoriteit Persoonsgegevens.

**Wat leer je?**

- De fundamenten van de AVG/GDPR wetgeving
- Privacy by design en privacy by default principes
- Uitvoeren van privacy-impact assessments (PIA's)
- Omgaan met datalekken en meldplicht
- Communiceren met betrokkenen over hun privacyrechten
- Adviseren over verwerkersovereenkomsten
- Toezicht houden op compliance binnen de organisatie

**Voor wie is deze opleiding?**

Deze opleiding is geschikt voor iedereen die als DPO wil werken of privacy-verantwoordelijkheden heeft binnen een organisatie. Voorkennis van juridische of ICT-onderwerpen is nuttig maar niet vereist.

**Studieduur en format**

De opleiding duurt 8 weken en bestaat uit e-learning modules die je in je eigen tempo doorloopt, aangevuld met praktijkgerichte workshops op locatie. Je kunt direct starten na inschrijving.`,
  2: `**Over de opleiding**

Leer in deze uitgebreide cursus alles over Microsoft Excel, van de basis tot geavanceerde functies. Je start met de fundamenten en bouwt stap voor stap je vaardigheden uit tot professioneel niveau.

Excel is een essentieel hulpmiddel in bijna elke functie en sector. Met deze opleiding ontwikkel je de vaardigheden om efficiënt te werken met spreadsheets, data te analyseren en professionele rapportages te maken.

**Wat leer je?**

- Basisfuncties: cellen, formules, en opmaak
- Werken met functies (SOM, ALS, VERT.ZOEKEN)
- Draaitabellen en grafieken maken
- Geavanceerde formules en functies
- Macros en automatisering
- Data-analyse en visualisatie
- Werken met grote datasets

**Voor wie is deze opleiding?**

Geschikt voor iedereen die Excel wil leren of hun vaardigheden wil verbeteren. Van beginners tot gevorderden die hun kennis willen uitbreiden.`,
};

const STORAGE_KEY = 'savedOpleidingen';

function generateEducations(count: number): Education[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    title: TITLES[i % TITLES.length],
    institution: INSTITUTIONS[i % INSTITUTIONS.length],
    duration: DURATIONS[i % DURATIONS.length],
    format: FORMATS[i % FORMATS.length],
    location: LOCATIONS[i % LOCATIONS.length],
    level: LEVELS[i % LEVELS.length],
    startDate: START_DATES[i % START_DATES.length],
    matchScore: Math.floor(60 + Math.random() * 40),
    diploma: ['Certificaat of bewijs van deelname', 'Certificaat', 'Diploma', 'Getuigschrift'][i % 4],
    accreditation: i % 3 === 0 ? 'NRTO' : undefined,
    description: DESCRIPTIONS[i + 1] || `**Over de opleiding**\n\nDeze opleiding ${TITLES[i % TITLES.length]} biedt een uitgebreid programma voor professionals die hun vaardigheden willen verbreden. Je leert de nieuwste inzichten en technieken binnen het vakgebied.\n\n**Wat leer je?**\n\n- Fundamentele concepten en principes\n- Praktische toepassingen\n- Best practices uit de sector\n- Samenwerken in teams\n\n**Voor wie is deze opleiding?**\n\nGeschikt voor professionals op ${LEVELS[i % LEVELS.length]} niveau die hun kennis willen uitbreiden.`,
  }));
}

function generateSkillEducations(skillName: string, count: number): Education[] {
  const titles = [
    `${skillName} voor beginners`,
    `Gevorderd ${skillName}`,
    `${skillName} Professional Certificate`,
    `Master ${skillName}`,
    `${skillName} Specialist Opleiding`,
    `${skillName} Bootcamp`,
    `${skillName} Essentials`,
    `${skillName} Expert Training`,
  ];

  return Array.from({ length: count }, (_, i) => ({
    id: i + 100,
    title: titles[i % titles.length],
    institution: INSTITUTIONS[i % INSTITUTIONS.length],
    duration: DURATIONS[i % DURATIONS.length],
    format: FORMATS[i % FORMATS.length],
    location: LOCATIONS[i % LOCATIONS.length],
    level: LEVELS[i % LEVELS.length],
    startDate: START_DATES[i % START_DATES.length],
    matchScore: Math.floor(60 + Math.random() * 40),
  }));
}

function generateSearchEducations(searchTerm: string, count: number): Education[] {
  const term = searchTerm || 'Algemene';
  const titles = [
    `${term} voor beginners`,
    `Gevorderd ${term}`,
    `${term} Professional Certificate`,
    `Master ${term}`,
    `${term} Specialist Opleiding`,
    `${term} Bootcamp`,
    `${term} Essentials`,
    `${term} Expert Training`,
  ];

  return Array.from({ length: count }, (_, i) => ({
    id: i + 200,
    title: titles[i % titles.length],
    institution: INSTITUTIONS[i % INSTITUTIONS.length],
    duration: DURATIONS[i % DURATIONS.length],
    format: FORMATS[i % FORMATS.length],
    location: LOCATIONS[i % LOCATIONS.length],
    level: LEVELS[i % LEVELS.length],
    startDate: START_DATES[i % START_DATES.length],
    matchScore: Math.floor(60 + Math.random() * 40),
  }));
}

@Injectable({ providedIn: 'root' })
export class EducationMockService {
  private educations = generateEducations(100);

  getEducations(): Observable<Education[]> {
    return of(this.educations);
  }

  getEducationById(id: number): Observable<Education | undefined> {
    return of(this.educations.find(e => e.id === id));
  }

  getEducationsBySkill(skillName: string): Observable<Education[]> {
    return of(generateSkillEducations(skillName, 50));
  }

  searchEducations(searchTerm: string): Observable<Education[]> {
    return of(generateSearchEducations(searchTerm, 50));
  }

  getSavedEducations(): Observable<Education[]> {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return of([]);
    return of(JSON.parse(saved) as Education[]);
  }

  getSavedIds(): number[] {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    return (JSON.parse(saved) as Education[]).map(e => e.id);
  }

  toggleSaveEducation(education: Education): boolean {
    const saved = localStorage.getItem(STORAGE_KEY);
    const list: Education[] = saved ? JSON.parse(saved) : [];
    const exists = list.some(e => e.id === education.id);

    if (exists) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.filter(e => e.id !== education.id)));
      return false;
    } else {
      list.push(education);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      return true;
    }
  }
}
